/**
 * Animated image collage
 * -------------------------------------------------------------
 * Marquee engine built on the Web Animations API, so the movement itself
 * lives on the compositor thread: no per-frame JavaScript, no main-thread
 * jank, and the browser keeps rasterising the photos at full resolution
 * instead of smearing them while they move.
 *
 *  - hover / focus          => smooth pause
 *  - grab & drag            => manual scrubbing (mouse, pen, touch) + inertia
 *  - trackpad / shift+wheel => scrubbing for the horizontal layout
 *  - prefers-reduced-motion, off-screen and hidden-tab aware
 *
 * The markup is the same one jquery.marquee produced
 * (.js-marquee-wrapper > .js-marquee), so the existing CSS keeps working.
 */
(() => {
  'use strict';

  if (!('animate' in Element.prototype)) return; // very old browser: stays static

  const REDUCE = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* ------------------------------------------------------------------ *
   * Tuning
   * ------------------------------------------------------------------ *
   * SPEED — global multiplier. 1 = the old jquery.marquee pace,
   * 1.6 = 60% faster. A single section can override it with
   * data-aic-speed="2" on its .aic element.
   * ------------------------------------------------------------------ */
  const SPEED = 1.6;

  const CYCLE_MS = { background: 50000, right: 40000 }; // ms per loop at SPEED = 1
  const STRIP_RATIO = { wide: 0.03, narrow: 0.08 }; // px/s per px of viewport width

  const TAU_STOP = 0.18; // s — how quickly it eases to a stop
  const TAU_GO = 0.85; // s — how quickly a fling blends back to cruise speed
  const DRAG_THRESHOLD = 6; // px before a pointer gesture becomes a drag
  const MIN_FLING = 60; // px/s below which a release is not a fling
  const GLIDE_EPS = 2; // px/s — when inertia has blended back into cruising

  const clamp = (v, min, max) => Math.min(Math.max(v, min), max);

  /* ------------------------------------------------------------------ *
   * Lane — one scrolling column (vertical) or one strip (horizontal)
   * ------------------------------------------------------------------ */
  class Lane {
    constructor(el, options) {
      this.el = el;
      this.axis = options.axis; // 'x' | 'y'
      this.direction = options.direction; // 1 = down/right, -1 = up/left
      this.durationBase = options.duration || null; // ms for one loop
      this.pxPerSecond = options.pxPerSecond || null;
      this.cycle = 0;
      this.durationMs = 0;
      this.player = null;
      this.rate = 1;
      this.velocity = 0;
      this.ready = false;

      this.build();
    }

    build() {
      const items = Array.from(this.el.children).filter(
        (node) => node.nodeType === 1 && !node.classList.contains('js-marquee-wrapper')
      );
      if (!items.length) return;

      const wrapper = document.createElement('div');
      wrapper.className = 'js-marquee-wrapper';

      const track = document.createElement('div');
      track.className = 'js-marquee';
      items.forEach((item) => track.appendChild(item));

      wrapper.appendChild(track);
      this.el.appendChild(wrapper);

      this.wrapper = wrapper;
      this.track = track;
      this.ready = true;
    }

    /** (Re)build the clones, measure the loop and (re)create the animation. */
    layout(viewport) {
      if (!this.ready) return;

      const progress = this.progress();

      if (this.player) {
        this.player.cancel();
        this.player = null;
      }
      while (this.wrapper.children.length > 1) {
        this.wrapper.removeChild(this.wrapper.lastElementChild);
      }

      const trackSize = this.axis === 'y' ? this.track.offsetHeight : this.track.offsetWidth;
      if (!trackSize) return;

      // one clone first, so the real loop length (track + gap) can be measured
      this.addClone();
      this.cycle = this.measureStep() || trackSize;

      // then only as many copies as the loop actually needs — a smaller
      // composited layer is what keeps the images sharp while they move
      const gap = Math.max(0, this.cycle - trackSize);
      const needed = Math.max(2, Math.ceil((viewport + this.cycle + gap) / this.cycle));
      while (this.wrapper.children.length < needed) this.addClone();

      const speed = this.durationBase
        ? this.cycle / (this.durationBase / 1000)
        : this.pxPerSecond || 0;
      this.durationMs = speed > 0 ? (this.cycle / speed) * 1000 : 0;
      if (!this.durationMs) return;

      this.createPlayer(progress);
    }

    addClone() {
      const clone = this.track.cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      clone.querySelectorAll('a, button, input, [tabindex]').forEach((node) => {
        node.setAttribute('tabindex', '-1');
      });
      this.wrapper.appendChild(clone);
    }

    measureStep() {
      const first = this.wrapper.children[0];
      const second = this.wrapper.children[1];
      if (!second) return 0;
      const step =
        this.axis === 'y' ? second.offsetTop - first.offsetTop : second.offsetLeft - first.offsetLeft;
      return step > 0 ? step : 0;
    }

    createPlayer(progress) {
      const from = 'translate3d(0, 0, 0)';
      const to =
        this.axis === 'y'
          ? `translate3d(0, ${-this.cycle}px, 0)`
          : `translate3d(${-this.cycle}px, 0, 0)`;

      // direction 1 runs the same loop the other way round
      const frames =
        this.direction === 1
          ? [{ transform: to }, { transform: from }]
          : [{ transform: from }, { transform: to }];

      this.player = this.wrapper.animate(frames, {
        duration: this.durationMs,
        iterations: Infinity,
        easing: 'linear'
      });
      this.player.currentTime = (progress || 0) * this.durationMs;
      this.applyRate();
    }

    progress() {
      if (!this.player || !this.durationMs) return 0;
      const t = Number(this.player.currentTime) || 0;
      return (t % this.durationMs) / this.durationMs;
    }

    /** ms of animation time per pixel of movement (sign aware) */
    get msPerPx() {
      return this.cycle ? (this.durationMs / this.cycle) * this.direction : 0;
    }

    /** px per second this lane travels at playback rate 1 */
    get cruiseVelocity() {
      return this.durationMs ? (this.cycle / (this.durationMs / 1000)) * this.direction : 0;
    }

    /**
     * Inertia after a flick. An infinite compositor animation cannot be
     * played backwards, so the short glide is scrubbed from JS and handed
     * back to the compositor the moment it has blended into cruising speed.
     */
    glide(dt, targetVelocity) {
      const tau = targetVelocity === 0 ? TAU_STOP : TAU_GO;
      this.velocity += (targetVelocity - this.velocity) * (1 - Math.exp(-dt / tau));
      this.scrub(this.velocity * dt);
      return Math.abs(this.velocity - targetVelocity) > GLIDE_EPS;
    }

    setRate(rate) {
      this.rate = rate;
      this.applyRate();
    }

    /** ease this lane's playback rate toward `target`; true while still easing */
    ease(dt, target) {
      const tau = target === 0 ? TAU_STOP : TAU_GO;
      this.rate += (target - this.rate) * (1 - Math.exp(-dt / tau));
      const settled = Math.abs(target - this.rate) < 0.01;
      if (settled) this.rate = target;
      this.applyRate();
      return !settled;
    }

    applyRate() {
      if (!this.player) return;
      if (this.rate < 0) this.rate = 0; // an infinite effect cannot run reversed
      if (this.rate < 0.002) {
        if (this.player.playState === 'running') this.player.pause();
        return;
      }
      this.player.playbackRate = this.rate;
      if (this.player.playState !== 'running') this.player.play();
    }

    hold() {
      if (this.player && this.player.playState === 'running') this.player.pause();
    }

    /** move the loop by `delta` pixels, independently of playback */
    scrub(delta) {
      if (!this.player || !this.durationMs) return;
      let t = (Number(this.player.currentTime) || 0) + delta * this.msPerPx;
      t = ((t % this.durationMs) + this.durationMs) % this.durationMs;
      this.player.currentTime = t;
    }

    /** current translate in px, read back from the animation */
    position() {
      const p = this.progress();
      const travel = -this.cycle * p;
      return this.direction === 1 ? -this.cycle - travel : travel;
    }

    destroy() {
      if (!this.ready) return;
      if (this.player) this.player.cancel();
      const originals = Array.from(this.track.children);
      this.wrapper.remove();
      originals.forEach((item) => this.el.appendChild(item));
      this.ready = false;
    }
  }

  /* ------------------------------------------------------------------ *
   * Collage — one .aic section: owns its lanes and all interaction
   * ------------------------------------------------------------------ */
  class Collage {
    constructor(root) {
      this.root = root;
      this.container = root.querySelector('.aic__marquees');
      if (!this.container) return;

      this.type = root.classList.contains('aic--bottom')
        ? 'bottom'
        : root.classList.contains('aic--right')
          ? 'right'
          : 'background';
      this.axis = this.type === 'bottom' ? 'x' : 'y';

      // Touch dragging is enabled where it cannot fight the page scroll:
      // always for the horizontal layout, opt-in for the vertical ones
      // via  data-aic-touch-drag="true"  on the section wrapper.
      this.touchDrag = this.axis === 'x' || root.dataset.aicTouchDrag === 'true';

      const custom = parseFloat(root.dataset.aicSpeed);
      this.speedFactor = custom > 0 ? custom : SPEED;

      this.lanes = [];
      this.stopped = false;
      this.dragging = false;
      this.pointerId = null;
      this.justDragged = false;
      this.gliding = false;
      this.visible = true;

      this.createLanes();
      if (!this.lanes.length) return;

      [
        'onPointerEnter',
        'onPointerLeave',
        'onPointerDown',
        'onPointerMove',
        'onPointerUp',
        'onClickCapture',
        'onWheel',
        'onFocusIn',
        'onFocusOut'
      ].forEach((name) => {
        this[name] = this[name].bind(this);
      });

      this.layout();
      this.bind();
      this.observe();
      this.sync(true);

      root.classList.add('aic--interactive');
      registry.add(this);
    }

    createLanes() {
      if (this.axis === 'x') {
        const strip = this.container.querySelector('.aic__marquee-horizontal');
        if (!strip) return;
        this.lanes.push(
          new Lane(strip, { axis: 'x', direction: -1, pxPerSecond: this.horizontalSpeed() })
        );
      } else {
        const duration = (CYCLE_MS[this.type] || CYCLE_MS.background) / this.speedFactor;
        this.container.querySelectorAll('.aic__marquee').forEach((el, index) => {
          const down = index % 2 === 0;
          el.classList.add(down ? 'marquee-down' : 'marquee-up');
          this.lanes.push(new Lane(el, { axis: 'y', direction: down ? 1 : -1, duration }));
        });
      }
      this.lanes = this.lanes.filter((lane) => lane.ready);
    }

    horizontalSpeed() {
      const w = document.documentElement.clientWidth;
      const ratio = w > 575 ? STRIP_RATIO.wide : STRIP_RATIO.narrow;
      return Math.round(w * ratio * this.speedFactor);
    }

    layout() {
      const viewport = this.axis === 'y' ? this.container.clientHeight : this.container.clientWidth;
      if (this.axis === 'x') {
        const speed = this.horizontalSpeed();
        this.lanes.forEach((lane) => {
          lane.pxPerSecond = speed;
        });
      }
      this.lanes.forEach((lane) => lane.layout(viewport));
      this.sync(true);
    }

    /* --------------------------- playback -------------------------- */

    get shouldRun() {
      return this.visible && !this.stopped && !this.dragging && !REDUCE.matches;
    }

    /** immediate = snap every lane to the target rate, otherwise ease into it */
    sync(immediate) {
      const target = this.shouldRun ? 1 : 0;
      if (immediate) {
        this.lanes.forEach((lane) => lane.setRate(target));
      } else {
        startTicker();
      }
    }

    frame(dt) {
      if (this.dragging) return false;
      const running = this.shouldRun;

      if (this.gliding) {
        let gliding = false;
        this.lanes.forEach((lane) => {
          if (lane.glide(dt, running ? lane.cruiseVelocity : 0)) gliding = true;
        });
        if (!gliding) {
          this.gliding = false;
          this.lanes.forEach((lane) => lane.setRate(running ? 1 : 0));
        }
        return true;
      }

      const target = running ? 1 : 0;
      let active = false;
      this.lanes.forEach((lane) => {
        if (lane.ease(dt, target)) active = true;
      });
      return active; // false = settled, all motion is compositor side again
    }

    /* ---------------------------- events --------------------------- */

    bind() {
      // hovering the whole hero feels right for the full-bleed variant,
      // the images themselves for the other two
      this.hoverTarget = this.type === 'background' ? this.root : this.container;
      this.hoverTarget.addEventListener('pointerenter', this.onPointerEnter);
      this.hoverTarget.addEventListener('pointerleave', this.onPointerLeave);
      this.hoverTarget.addEventListener('focusin', this.onFocusIn);
      this.hoverTarget.addEventListener('focusout', this.onFocusOut);

      this.container.addEventListener('pointerdown', this.onPointerDown);
      this.container.addEventListener('click', this.onClickCapture, true);
      if (this.axis === 'x') {
        this.container.addEventListener('wheel', this.onWheel, { passive: false });
      }
      this.container.classList.add('aic__marquees--draggable');
    }

    onPointerEnter(event) {
      if (event.pointerType && event.pointerType !== 'mouse') return;
      this.stopped = true;
      this.sync();
    }

    onPointerLeave(event) {
      if (event.pointerType && event.pointerType !== 'mouse') return;
      if (this.dragging) return;
      this.stopped = false;
      this.sync();
    }

    onFocusIn() {
      this.stopped = true;
      this.sync();
    }

    onFocusOut() {
      this.stopped = false;
      this.sync();
    }

    position(event) {
      return this.axis === 'y' ? event.clientY : event.clientX;
    }

    onPointerDown(event) {
      if (this.pointerId !== null) return;
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      if (event.pointerType === 'touch' && !this.touchDrag) return;

      this.pointerId = event.pointerId;
      this.dragging = false;
      this.moved = 0;
      this.lastPosition = this.position(event);
      this.lastMoveTime = event.timeStamp || performance.now();
      this.dragVelocity = 0;
      this.justDragged = false;

      window.addEventListener('pointermove', this.onPointerMove, { passive: false });
      window.addEventListener('pointerup', this.onPointerUp);
      window.addEventListener('pointercancel', this.onPointerUp);
    }

    onPointerMove(event) {
      if (event.pointerId !== this.pointerId) return;

      const position = this.position(event);
      const delta = position - this.lastPosition;
      this.moved += Math.abs(delta);

      if (!this.dragging) {
        if (this.moved < DRAG_THRESHOLD) {
          this.lastPosition = position;
          return;
        }
        this.dragging = true;
        this.gliding = false;
        this.lanes.forEach((lane) => lane.hold());
        this.root.classList.add('aic--dragging');
        if (this.container.setPointerCapture) {
          try {
            this.container.setPointerCapture(event.pointerId);
          } catch (e) {
            /* no-op */
          }
        }
      }

      const now = event.timeStamp || performance.now();
      const dt = Math.max(now - this.lastMoveTime, 1) / 1000;
      this.dragVelocity = clamp(delta / dt, -6000, 6000);
      this.lastPosition = position;
      this.lastMoveTime = now;

      this.lanes.forEach((lane) => lane.scrub(delta));
      if (event.cancelable) event.preventDefault();
    }

    onPointerUp(event) {
      if (event.pointerId !== this.pointerId) return;

      window.removeEventListener('pointermove', this.onPointerMove);
      window.removeEventListener('pointerup', this.onPointerUp);
      window.removeEventListener('pointercancel', this.onPointerUp);

      const wasDragging = this.dragging;
      this.pointerId = null;
      this.dragging = false;
      this.root.classList.remove('aic--dragging');

      if (wasDragging) {
        this.justDragged = true;
        window.setTimeout(() => {
          this.justDragged = false;
        }, 300);
      }

      // a mouse still sitting on the section keeps it paused
      this.stopped = event.pointerType === 'mouse' && this.hoverTarget.matches(':hover');

      if (wasDragging && event.type !== 'pointercancel' && Math.abs(this.dragVelocity) > MIN_FLING) {
        this.gliding = true;
        this.lanes.forEach((lane) => {
          lane.hold();
          lane.velocity = this.dragVelocity;
          lane.rate = 0;
        });
        startTicker();
      } else {
        this.gliding = false;
        this.sync();
      }
    }

    onClickCapture(event) {
      if (!this.justDragged) return;
      event.preventDefault();
      event.stopPropagation();
    }

    onWheel(event) {
      const horizontal = Math.abs(event.deltaX) > Math.abs(event.deltaY);
      const delta = horizontal ? event.deltaX : event.shiftKey ? event.deltaY : 0;
      if (!delta) return; // a plain vertical wheel keeps scrolling the page
      event.preventDefault();
      this.lanes.forEach((lane) => lane.scrub(-delta));
    }

    /* -------------------------- observers -------------------------- */

    observe() {
      if ('IntersectionObserver' in window) {
        this.intersectionObserver = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              this.visible = entry.isIntersecting;
              this.sync(true);
            });
          },
          { rootMargin: '200px' }
        );
        this.intersectionObserver.observe(this.root);
      }

      if ('ResizeObserver' in window) {
        let frameId = null;
        this.resizeObserver = new ResizeObserver(() => {
          if (frameId) cancelAnimationFrame(frameId);
          frameId = requestAnimationFrame(() => {
            frameId = null;
            this.layout();
          });
        });
        this.resizeObserver.observe(this.container);
      } else {
        this.onResize = () => this.layout();
        window.addEventListener('resize', this.onResize);
      }

      // images can change the measured length once they land
      this.onLoad = () => this.layout();
      window.addEventListener('load', this.onLoad);
    }

    destroy() {
      registry.delete(this);
      if (this.intersectionObserver) this.intersectionObserver.disconnect();
      if (this.resizeObserver) this.resizeObserver.disconnect();
      if (this.onResize) window.removeEventListener('resize', this.onResize);
      if (this.onLoad) window.removeEventListener('load', this.onLoad);
      this.hoverTarget.removeEventListener('pointerenter', this.onPointerEnter);
      this.hoverTarget.removeEventListener('pointerleave', this.onPointerLeave);
      this.hoverTarget.removeEventListener('focusin', this.onFocusIn);
      this.hoverTarget.removeEventListener('focusout', this.onFocusOut);
      this.container.removeEventListener('pointerdown', this.onPointerDown);
      this.container.removeEventListener('click', this.onClickCapture, true);
      this.container.removeEventListener('wheel', this.onWheel);
      window.removeEventListener('pointermove', this.onPointerMove);
      window.removeEventListener('pointerup', this.onPointerUp);
      window.removeEventListener('pointercancel', this.onPointerUp);
      this.lanes.forEach((lane) => lane.destroy());
      this.lanes = [];
      this.root.classList.remove('aic--interactive');
      delete this.root.aicCollage;
    }
  }

  /* ------------------------------------------------------------------ *
   * Shared ticker — only runs while a rate is being eased, never while
   * the marquee is simply cruising (that is all compositor side).
   * ------------------------------------------------------------------ */
  const registry = new Set();
  let rafId = null;
  let lastTime = 0;

  function frame(now) {
    const dt = lastTime ? Math.min((now - lastTime) / 1000, 0.05) : 0;
    lastTime = now;

    let active = false;
    registry.forEach((collage) => {
      if (collage.frame(dt)) active = true;
    });

    if (active && !document.hidden) {
      rafId = requestAnimationFrame(frame);
    } else {
      rafId = null;
      lastTime = 0;
    }
  }

  function startTicker() {
    if (rafId !== null || document.hidden) return;
    lastTime = 0;
    rafId = requestAnimationFrame(frame);
  }

  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) startTicker();
  });

  if (REDUCE.addEventListener) {
    REDUCE.addEventListener('change', () => {
      registry.forEach((collage) => collage.sync(true));
    });
  }

  /* ------------------------------------------------------------------ *
   * Boot
   * ------------------------------------------------------------------ */
  function init(scope) {
    const root = scope && scope.querySelectorAll ? scope : document;
    root.querySelectorAll('.aic').forEach((section) => {
      if (section.aicCollage) return;
      section.aicCollage = new Collage(section);
    });
    if (root !== document && root.matches && root.matches('.aic') && !root.aicCollage) {
      root.aicCollage = new Collage(root);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:reorder', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll('.aic').forEach((section) => {
      if (section.aicCollage) section.aicCollage.destroy();
    });
  });
})();